import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Mail, Loader2, CheckCircle2, Inbox, RefreshCw, Plus, Trash2, Pencil } from 'lucide-react';
import { axiosInstance } from '../App';

const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRESETS = {
  jouwweb: { host: 'imap.jouwweb.email', port: 993, hint: 'Jouwweb / Argeweb mailbox' },
  gmail: { host: 'imap.gmail.com', port: 993, hint: 'Gmail — gebruik een App Password (2FA vereist)' },
  outlook: { host: 'outlook.office365.com', port: 993, hint: 'Outlook / Microsoft 365' },
  custom: { host: '', port: 993, hint: 'Eigen IMAP-server' },
};

const EMPTY_FORM = {
  account_id: null,
  label: '',
  preset: 'jouwweb',
  host: 'imap.jouwweb.email',
  port: 993,
  user: '',
  password: '',
  folder: 'INBOX',
  subject_filters: 'factuur, invoice, rekening, aanmaning',
  max_count: 25,
  enabled: true,
};

const fmtD = (iso) => {
  if (!iso) return '?';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
};

const EmailImportSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(null); // null = form hidden
  const [lastSync, setLastSync] = useState(null);
  const [progress, setProgress] = useState(null);
  const [fullSince, setFullSince] = useState(`${new Date().getFullYear()}-01-01`);
  const [history, setHistory] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [accR, histR] = await Promise.all([
        axiosInstance.get('/email-import/accounts'),
        axiosInstance.get('/email-import/history?limit=15').catch(() => ({ data: { items: [] } })),
      ]);
      setAccounts(accR.data?.accounts || []);
      setHistory(histR.data?.items || []);
    } catch (e) {
      toast.error('Kon instellingen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openNew = () => setForm({ ...EMPTY_FORM });
  const openEdit = (acc) => setForm({
    ...EMPTY_FORM,
    account_id: acc.id,
    label: acc.label || '',
    host: acc.host,
    port: acc.port,
    user: acc.user,
    folder: acc.folder || 'INBOX',
    subject_filters: (acc.subject_filters || []).join(', ') || EMPTY_FORM.subject_filters,
    max_count: acc.max_count || 25,
    enabled: acc.enabled !== false,
    preset: 'custom',
  });

  const handlePreset = (key) => {
    const p = PRESETS[key];
    setForm((prev) => ({ ...prev, preset: key, host: p.host || prev.host, port: p.port }));
  };

  const normalizeImap = () => {
    let host = form.host.trim().toLowerCase();
    let port = Number(form.port) || 993;
    if (host.startsWith('smtp.')) host = 'imap.' + host.slice(5);
    if ([25, 465, 587].includes(port)) port = 993;
    if (host !== form.host.trim() || port !== Number(form.port)) {
      setForm((prev) => ({ ...prev, host, port }));
      toast.info(`Instellingen gecorrigeerd naar IMAP: ${host}:${port} (smtp/587 is voor versturen, niet ophalen)`);
    }
    return { host, port };
  };

  const buildPayload = () => ({
    account_id: form.account_id || undefined,
    label: form.label.trim() || undefined,
    ...normalizeImap(),
    user: form.user.trim(),
    password: form.password || undefined,
    folder: form.folder.trim() || 'INBOX',
    subject_filters: form.subject_filters.split(',').map((s) => s.trim()).filter(Boolean),
    max_count: Number(form.max_count) || 25,
    enabled: !!form.enabled,
  });

  const handleSave = async () => {
    if (!form.host || !form.user) return toast.error('Host en e-mail zijn verplicht');
    if (!form.account_id && !form.password) return toast.error('Wachtwoord is verplicht bij een nieuw account');
    setSaving(true);
    try {
      await axiosInstance.post('/email-import/accounts', buildPayload());
      toast.success('Account opgeslagen');
      setForm(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await axiosInstance.post('/email-import/test', buildPayload());
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test mislukt');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(`Account "${acc.label || acc.user}" verwijderen?`)) return;
    try {
      await axiosInstance.delete(`/email-import/accounts/${acc.id}`);
      toast.success('Account verwijderd');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const toggleEnabled = async (acc, v) => {
    try {
      await axiosInstance.post('/email-import/accounts', {
        account_id: acc.id, label: acc.label, host: acc.host, port: acc.port, user: acc.user,
        folder: acc.folder || 'INBOX', subject_filters: acc.subject_filters, max_count: acc.max_count || 25, enabled: v,
      });
      loadAll();
    } catch (e) {
      toast.error('Wijzigen mislukt');
    }
  };

  const followProgress = async () => {
    setSyncing(true);
    try {
      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 2000));
        const pr = await axiosInstance.get('/email-import/progress');
        setProgress(pr.data);
        done = !pr.data.running;
      }
      const s = (await axiosInstance.get('/email-import/progress')).data.result || {};
      if (s.ok === false && s.error) toast.error(s.error, { duration: 10000 });
      if (s.ok !== false || (s.processed ?? 0) > 0) {
        const extra = [
          s.skipped_duplicate ? `${s.skipped_duplicate} dubbel overgeslagen` : null,
          s.skipped_not_invoice ? `${s.skipped_not_invoice} PDF's geen factuur` : null,
          s.errors_count ? `${s.errors_count} fout(en)` : null,
          s.leads_found ? `${s.leads_found} aanvra(a)g(en)` : null,
        ].filter(Boolean).join(', ');
        toast.success(`${s.processed ?? 0} factu(u)r(en) verwerkt (${fmtD(s.since)} t/m ${fmtD(s.until)}) — ${s.inkoop_count ?? 0} inkoop, ${s.expense_count ?? 0} uitgave${extra ? ` · ${extra}` : ''}`, { duration: 8000 });
      }
      setLastSync(s);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Synchronisatie mislukt');
    } finally {
      setSyncing(false);
      setProgress(null);
    }
  };

  const [jobBusy, setJobBusy] = useState('');
  const [jobInfo, setJobInfo] = useState('');
  const runJob = async (endpoint, jobId, label) => {
    setJobBusy(jobId); setJobInfo(`${label}: gestart...`);
    try {
      const st = await axiosInstance.post(`/email-import/${endpoint}`);
      if (st.data.already_running) toast.info(`${label} loopt al`);
      let doc = { running: true };
      while (doc.running) {
        await new Promise((r) => setTimeout(r, 2500));
        doc = (await axiosInstance.get(`/email-import/jobs/${jobId}`)).data;
        if (doc.running) setJobInfo(`${label}: ${doc.checked || 0} van ${doc.total || '?'} gecontroleerd${jobId === 'backfill' ? `, ${doc.attached || 0} bijlagen gekoppeld` : `, ${doc.updated || 0} ingedeeld`}`);
      }
      const r = doc.result || {};
      if (r.error) { toast.error(r.error, { duration: 10000 }); setJobInfo(`${label}: mislukt`); }
      else if (jobId === 'backfill') { toast.success(`${r.attached || 0} bijlage(n) gekoppeld · ${r.not_found || 0} niet gevonden in mailbox · ${r.total || 0} gecontroleerd`, { duration: 10000 }); setJobInfo(`Bijlagen: ${r.attached || 0} gekoppeld, ${r.not_found || 0} niet gevonden`); }
      else { toast.success(`${r.total || 0} uitgaven herzien · ${r.updated || 0} categorie(ën) aangepast (${r.by_rule || 0} via geleerde regels, ${r.by_ai || 0} via AI)`, { duration: 8000 }); setJobInfo(`Categorieën: ${r.updated || 0} van ${r.total || 0} aangepast`); }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} mislukt`); setJobInfo('');
    } finally {
      setJobBusy('');
    }
  };

  const handleSync = async (opts = null) => {
    setSyncing(true);
    setProgress({ running: true, processed: 0, total: 0, phase: 'verbinden', since: opts?.since });
    try {
      const start = await axiosInstance.post('/email-import/sync', opts || {});
      if (start.data.already_running) toast.info('Synchronisatie loopt al...');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Synchronisatie mislukt');
      setSyncing(false);
      setProgress(null);
      return;
    }
    await followProgress();
  };

  useEffect(() => {
    axiosInstance.get('/email-import/progress').then((pr) => {
      if (pr.data?.running) { setProgress(pr.data); followProgress(); }
      else if (pr.data?.result) {
        setLastSync(pr.data.result);
        if (pr.data.result.ok === false && pr.data.result.error) toast.error(pr.data.result.error, { duration: 10000 });
      }
    }).catch(() => {});
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-600"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Laden...</div>;
  }

  return (
    <div className="space-y-6" data-testid="email-import-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">E-mail facturen importeren</h1>
          <p className="text-sm text-gray-500 mt-1">
            Koppel één of meerdere mailboxen — facturen worden automatisch (elke ochtend om 06:30 + op verzoek) uitgelezen en in Inkoop of Uitgaven geplaatst.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openNew} variant="outline" className="border-[#1a5276] text-[#1a5276]" data-testid="email-import-add-account-btn">
            <Plus className="w-4 h-4 mr-1" /> Account toevoegen
          </Button>
          <Button onClick={() => handleSync()} disabled={syncing || accounts.length === 0} className="bg-[#3da5a5] hover:bg-[#2d8585] text-white" data-testid="email-import-sync-now-btn">
            {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Synchroniseren...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Nu synchroniseren</>}
          </Button>
        </div>
      </div>

      <Card className="border-gray-200" data-testid="email-import-tools-card">
        <CardContent className="py-3 flex items-center gap-2 flex-wrap text-xs text-gray-600">
          <span className="font-semibold text-gray-800 mr-1">Eerder geïmporteerde facturen:</span>
          <Button size="sm" variant="outline" disabled={!!jobBusy} onClick={() => runJob('backfill-attachments', 'backfill', 'Bijlagen ophalen')} data-testid="email-import-backfill-btn">
            {jobBusy === 'backfill' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Bijlagen (PDF) ophalen uit mailbox
          </Button>
          <Button size="sm" variant="outline" disabled={!!jobBusy} onClick={() => runJob('recategorize', 'recategorize', 'Categorieën')} data-testid="email-import-recategorize-btn">
            {jobBusy === 'recategorize' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Categorieën herzien (geleerde regels + AI)
          </Button>
          <Button size="sm" className="bg-[#1a5276] hover:bg-[#154360] text-white" disabled={!!jobBusy} onClick={async () => { await runJob('backfill-attachments', 'backfill', 'Bijlagen ophalen'); await runJob('recategorize', 'recategorize', 'Categorieën'); }} data-testid="email-import-review-all-btn">
            {jobBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Alles hercontroleren (bijlagen + categorieën)
          </Button>
          {jobInfo && <span data-testid="email-import-job-status">{jobInfo}</span>}
        </CardContent>
      </Card>

      <Card className="border-[#1a5276]/20" data-testid="email-import-full-card">
        <CardContent className="py-4 flex flex-wrap items-end gap-3">
          <div>
            <p className="text-sm font-semibold text-[#1a5276]">Volledige import — hele mailbox doorzoeken</p>
            <p className="text-xs text-gray-500 mt-0.5">Doorzoekt álle e-mails vanaf de gekozen datum (zonder limiet van 25). Al geïmporteerde facturen worden automatisch overgeslagen.</p>
          </div>
          <div className="flex items-end gap-2 ml-auto">
            <div>
              <Label className="text-xs text-gray-600">Zoeken vanaf</Label>
              <Input type="date" value={fullSince} onChange={(e) => setFullSince(e.target.value)} className="w-40" data-testid="email-import-full-since-input" />
            </div>
            <Button onClick={() => handleSync({ since: fullSince, full: true })} disabled={syncing || accounts.length === 0 || !fullSince}
              variant="outline" className="border-[#1a5276] text-[#1a5276] hover:bg-[#1a5276]/5" data-testid="email-import-full-sync-btn">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Inbox className="w-4 h-4 mr-2" />} Volledige import starten
            </Button>
          </div>
        </CardContent>
      </Card>

      {progress && progress.running !== false && (
        <Card className="border-[#3da5a5]/40 bg-[#3da5a5]/5" data-testid="email-import-progress">
          <CardContent className="py-4 space-y-2">
            {(() => {
              const phase = progress.phase || 'verbinden';
              const found = progress.found || 0, scanned = Math.min(progress.scanned || 0, found);
              const total = progress.total || 0, processed = Math.min(progress.processed || 0, total);
              const steps = { verbinden: 1, zoeken: 1, downloaden: 2, verwerken: 3, aanvragen: 4 };
              let label = 'Verbinden met mailbox...';
              let pct = 3;
              if (phase === 'zoeken') { label = `Mailbox doorzoeken${progress.since ? ` vanaf ${progress.since}` : ''}...`; pct = 6; }
              if (phase === 'downloaden') { label = `${found} mails met PDF gevonden — controleren: ${scanned} van ${found}`; pct = 8 + (found ? Math.round((scanned / found) * 32) : 0); }
              if (phase === 'verwerken') { label = total ? `Facturen lezen met AI: ${processed} van ${total} PDF's` : 'Geen nieuwe facturen gevonden'; pct = 40 + (total ? Math.round((processed / total) * 58) : 58); }
              if (phase === 'aanvragen') { label = 'Offerte-aanvragen scannen...'; pct = 99; }
              return (
                <>
                  <div className="flex items-center justify-between text-sm gap-4">
                    <p className="font-semibold text-[#1a5276] flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Stap {steps[phase] || 1}/4{progress.account ? ` — ${progress.account}` : ''}
                    </p>
                    <p className="text-gray-700 font-medium text-right" data-testid="email-import-progress-count">{label}</p>
                  </div>
                  <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-[#3da5a5] rounded-full transition-all duration-500" style={{ width: `${Math.max(3, Math.min(100, pct))}%` }} />
                  </div>
                  <p className="text-xs text-gray-500" data-testid="email-import-progress-pct">
                    {Math.min(100, pct)}% · {processed} verwerkt{progress.emails_found ? ` · ${progress.emails_found} mails met factuur` : ''}
                    {progress.since && <> · periode {fmtD(progress.since)} t/m vandaag</>}
                    {progress.current_email_date && <> · nu bij mail van <strong>{fmtD(progress.current_email_date)}</strong></>}
                    {progress.oldest_email_date && <> · terug tot {fmtD(progress.oldest_email_date)}</>}
                  </p>
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1a5276]">
            <Mail className="w-5 h-5" /> Gekoppelde e-mailaccounts ({accounts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {accounts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Nog geen accounts gekoppeld. Klik op "Account toevoegen".</p>
          ) : (
            <div className="space-y-2">
              {accounts.map((acc) => (
                <div key={acc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100 flex-wrap gap-2" data-testid={`email-account-${acc.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-semibold text-gray-800">{acc.label || acc.user}</p>
                    <p className="text-xs text-gray-500">{acc.user} · {acc.host}</p>
                    {acc.password_invalid && (
                      <button type="button" onClick={() => openEdit(acc)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5 hover:bg-red-100" data-testid={`email-account-password-invalid-${acc.id}`}>
                        ⚠ Wachtwoord opnieuw invoeren — klik hier
                      </button>
                    )}
                    {acc.last_sync?.last_sync_at && (
                      <p className="text-[11px] text-gray-400" data-testid={`email-account-lastsync-${acc.id}`}>
                        Laatste sync: {new Date(acc.last_sync.last_sync_at).toLocaleString('nl-NL')} — {acc.last_sync.processed ?? 0} verwerkt
                        {acc.last_sync.since && <> · doorzocht {fmtD(acc.last_sync.since)} t/m {fmtD(acc.last_sync.until)}</>}
                        {acc.last_sync.oldest_email_date && <> · facturen van {fmtD(acc.last_sync.oldest_email_date)} t/m {fmtD(acc.last_sync.newest_email_date)}</>}
                        {acc.last_sync.skipped_duplicate ? <> · {acc.last_sync.skipped_duplicate} dubbel overgeslagen</> : null}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={acc.enabled !== false} onCheckedChange={(v) => toggleEnabled(acc, v)} data-testid={`email-account-toggle-${acc.id}`} />
                    <Button variant="ghost" size="sm" onClick={() => openEdit(acc)} data-testid={`email-account-edit-${acc.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(acc)} className="text-red-500" data-testid={`email-account-delete-${acc.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {form && (
        <Card className="border-[#1a5276]/40">
          <CardHeader>
            <CardTitle className="text-[#1a5276] text-base">{form.account_id ? 'Account bewerken' : 'Nieuw account'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-800 mb-2 block">E-mail-provider</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(PRESETS).map(([key, p]) => (
                  <button key={key} type="button" onClick={() => handlePreset(key)}
                    className={`text-left p-3 rounded border-2 transition-all ${form.preset === key ? 'border-[#3da5a5] bg-[#3da5a5]/10' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`email-preset-${key}`}>
                    <p className="font-semibold text-sm capitalize">{key}</p>
                    <p className="text-[11px] text-gray-500">{p.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-800 mb-2 block">Naam / label</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="bijv. Info mailbox" data-testid="email-import-label" />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">IMAP host *</Label>
                <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="imap.jouwweb.email" data-testid="email-import-host" />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">E-mailadres *</Label>
                <Input type="email" value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} placeholder="info@cardosobouw.nl" data-testid="email-import-user" />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">
                  Wachtwoord {form.account_id && <span className="text-xs text-gray-500 font-normal">(laat leeg om bestaande te behouden)</span>}
                </Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: form.preset === 'gmail' ? e.target.value.replace(/\s+/g, '') : e.target.value })} placeholder={form.account_id ? '••••••• (opgeslagen)' : form.preset === 'gmail' ? 'App-wachtwoord (16 tekens)' : 'IMAP-wachtwoord'} data-testid="email-import-password" />
              </div>
              {form.preset === 'gmail' && (
                <div className="md:col-span-2 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="email-import-gmail-hint">
                  <p className="font-semibold mb-1">Gmail accepteert je gewone wachtwoord niet — gebruik een App-wachtwoord:</p>
                  <ol className="list-decimal ml-4 space-y-0.5">
                    <li>Zet <a href="https://myaccount.google.com/signinoptions/two-step-verification" target="_blank" rel="noreferrer" className="underline">Verificatie in 2 stappen</a> aan voor {form.user || 'je Gmail-account'}.</li>
                    <li>Maak een <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">App-wachtwoord</a> aan (naam bijv. "Cardoso Portaal").</li>
                    <li>Plak de code van 16 tekens hierboven als wachtwoord en klik op <b>Verbinding testen</b>.</li>
                  </ol>
                </div>
              )}
              <div>
                <Label className="text-gray-800 mb-2 block">Poort</Label>
                <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} data-testid="email-import-port" />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">Map (folder)</Label>
                <Input value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} placeholder="INBOX" />
              </div>
            </div>

            <div>
              <Label className="text-gray-800 mb-2 block">Extra trefwoorden in onderwerp (komma-gescheiden) <span className="text-xs text-gray-500 font-normal">— alle mails met een PDF-bijlage worden sowieso gecontroleerd</span></Label>
              <Input value={form.subject_filters} onChange={(e) => setForm({ ...form, subject_filters: e.target.value })} placeholder="factuur, invoice, rekening" />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} id="enabled-switch" />
              <Label htmlFor="enabled-switch" className="text-gray-800 cursor-pointer">Import actief</Label>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-100">
              <Button onClick={handleTest} disabled={testing} variant="outline" className="border-[#3da5a5] text-[#3da5a5]" data-testid="email-import-test-btn">
                {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testen...</> : 'Verbinding testen'}
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#1a5276] hover:bg-[#15466a] text-white" data-testid="email-import-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opslaan...</> : 'Opslaan'}
              </Button>
              <Button onClick={() => setForm(null)} variant="ghost" data-testid="email-import-cancel-btn">Annuleren</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {lastSync && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1a5276] text-base">
              <Inbox className="w-5 h-5" /> Laatste synchronisatie
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-gray-700">
              {lastSync.processed} factu(u)r(en) verwerkt — <strong className="text-[#1a5276]">{lastSync.inkoop_count}</strong> inkoop, <strong className="text-amber-600">{lastSync.expense_count}</strong> uitgave
              {lastSync.last_sync_at && <span className="text-gray-500"> · {new Date(lastSync.last_sync_at).toLocaleString('nl-NL')}</span>}
            </p>
            <p className="text-xs text-gray-600" data-testid="email-import-lastsync-range">
              Doorzocht: <strong>{fmtD(lastSync.since)} t/m {fmtD(lastSync.until)}</strong>
              {lastSync.oldest_email_date && <> · facturen gevonden van {fmtD(lastSync.oldest_email_date)} t/m {fmtD(lastSync.newest_email_date)}</>}
              {lastSync.skipped_duplicate ? <> · <span className="text-amber-700">{lastSync.skipped_duplicate} dubbel overgeslagen</span></> : null}
              {lastSync.skipped_not_invoice ? <> · {lastSync.skipped_not_invoice} PDF's geen factuur</> : null}
            </p>
            {(lastSync.accounts || []).map((a, i) => (
              <p key={i} className="text-xs text-gray-600">
                • <strong>{a.account}</strong>: {a.ok === false ? <span className="text-red-600">{a.error}</span> : `${a.processed || 0} verwerkt (${a.inkoop_count || 0} inkoop, ${a.expense_count || 0} uitgave${a.skipped_duplicate ? `, ${a.skipped_duplicate} dubbel` : ''})`}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-[#1a5276] text-base">Recent automatisch geïmporteerd</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Nog niets geïmporteerd. Klik op "Nu synchroniseren" om te beginnen.</p>
          ) : (
            <div className="space-y-2">
              {history.map((it, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100" data-testid={`email-import-history-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${it.kind === 'inkoop' ? 'bg-[#1a5276] text-white' : 'bg-amber-500 text-white'}`}>
                        {it.kind === 'inkoop' ? 'INKOOP' : 'UITGAVE'}
                      </span>
                      <span className="text-sm font-semibold text-gray-800 truncate">{it.vendor_name || it.vendor || 'Onbekend'}</span>
                      {it.import_confidence !== undefined && (
                        <span className="text-[10px] text-gray-500">AI {Math.round((it.import_confidence || 0) * 100)}%</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {it.invoice_number ? `${it.invoice_number} · ` : ''}{it.description || it.import_source?.email_subject || ''}
                    </p>
                  </div>
                  <div className="text-right ml-3 flex-shrink-0">
                    <p className="text-sm font-bold text-gray-800">{fmtEuro(it.total_incl_btw || it.amount)}</p>
                    <p className="text-[11px] text-gray-500">{it.invoice_date || it.date || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4 text-xs text-blue-900 space-y-1">
          <p className="font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Hoe werkt dit?</p>
          <p>1. Voeg één of meerdere e-mailaccounts toe en test de verbinding.</p>
          <p>2. We lezen <strong>alle e-mails met een PDF-bijlage</strong> vanaf de gekozen datum (bij Gmail ook gearchiveerde mails; eigen verstuurde mails en niet-facturen worden overgeslagen) — automatisch elke ochtend (06:30), via "Nu synchroniseren", of via de Assistent. Al verwerkte facturen worden overgeslagen (geen dubbelen).</p>
          <p>3. Per keer worden max. 25 nieuwe facturen per account verwerkt — klik nogmaals op synchroniseren voor de rest, of de nachtelijke sync pakt ze op.</p>
          <p>4. Elke PDF-bijlage wordt door AI gelezen en automatisch in Inkoop of Uitgaven geplaatst.</p>
          <p className="pt-1">⚠️ De AI doet zijn best, maar kan af en toe een veld missen. Controleer altijd kort de bedragen.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailImportSettings;
